import { useEffect, useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useStore } from '../store/useStore';
import { useT } from '../i18n/useT';
import { getDocument, setDocTags, setDocTagsMirror } from '../lib/library';
import { listDocumentMeta } from '../lib/docMeta';
import { allTags, dedupeTags, normalizeTag } from '../lib/tags';
import { declaredTags, writeTags } from '../lib/metadata';
import { inlineTags } from '../lib/inlineTags';
import { Icon } from './Icon';
import { Modal } from './Modal';

/** Add/remove organizational tags on a document, with suggestions. */
export function TagsEditorDialog() {
  const t = useT();
  const docId = useStore((s) => s.tagsEditorFor);
  const setDocId = useStore((s) => s.setTagsEditorFor);
  const [tags, setTags] = useState<string[]>([]);
  // Tags written as `#tag` in the prose. Shown so the list is complete, but not
  // editable here: removing one means editing the sentence it lives in.
  const [inline, setInline] = useState<string[]>([]);
  const [input, setInput] = useState('');
  const docs = useLiveQuery(() => listDocumentMeta(), [], []);
  const activeId = useStore((s) => s.activeDoc.id);
  const setContent = useStore((s) => s.setContent);

  useEffect(() => {
    if (!docId) return;
    void getDocument(docId).then((d) => {
      setTags(d ? declaredTags(d) : []);
      setInline(d ? inlineTags(d.content) : []);
    });
    setInput('');
  }, [docId]);

  const suggestions = useMemo(() => {
    const have = new Set(tags.map((x) => x.toLowerCase()));
    return allTags(docs).filter((x) => !have.has(x.toLowerCase()));
  }, [docs, tags]);

  if (!docId) return null;

  const persist = (next: string[]) => {
    const clean = dedupeTags(next);
    setTags(clean);
    // Tags live in the document's frontmatter. For the document currently in the
    // editor that write has to go through the buffer — writing straight to the
    // database would be overwritten by the next autosave of the stale content.
    if (docId === activeId) {
      setContent(writeTags(useStore.getState().activeDoc.content, clean));
      void setDocTagsMirror(docId, clean);
    } else {
      void setDocTags(docId, clean);
    }
  };
  const add = (raw: string) => {
    const n = normalizeTag(raw);
    if (n) persist([...tags, n]);
    setInput('');
  };
  const remove = (tag: string) => persist(tags.filter((x) => x !== tag));

  const close = () => setDocId(null);

  return (
    <Modal onClose={close} title={t('tags.title')}>
      <div className="mb-tag-chips">
        {tags.length === 0 && inline.length === 0 && (
          <span className="mb-modal-note">{t('tags.none')}</span>
        )}
        {tags.map((tag) => (
          <span key={tag} className="mb-tag-chip mb-tag-chip--editable">
            {tag}
            <button aria-label={t('tags.remove', { tag })} onClick={() => remove(tag)}>
              <Icon name="x" size={12} />
            </button>
          </span>
        ))}
        {inline.map((tag) => (
          <span
            key={`inline-${tag}`}
            className="mb-tag-chip mb-tag-chip--inline"
            title={t('tags.inline')}
          >
            #{tag}
          </span>
        ))}
      </div>

      <input
        className="mb-modal-url"
        autoFocus
        placeholder={t('tags.placeholder')}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add(input);
          } else if (e.key === 'Backspace' && !input && tags.length) {
            remove(tags[tags.length - 1]!);
          }
        }}
        aria-label={t('tags.placeholder')}
      />

      {suggestions.length > 0 && (
        <div className="mb-tag-suggest">
          {suggestions.slice(0, 12).map((s) => (
            <button key={s} className="mb-tag-chip" onClick={() => add(s)}>
              + {s}
            </button>
          ))}
        </div>
      )}

      <div className="mb-modal-actions">
        <button className="mb-btn mb-btn-primary" onClick={close}>
          {t('common.done')}
        </button>
      </div>
    </Modal>
  );
}
